import { cn } from "@/lib/utils";
import { Shield, ShieldCheck, ShieldAlert, AlertTriangle, Quote, Ban } from "lucide-react";
import type { OnboardingContext } from "./Phase11CommitGate";

const TEST_LABELS: Record<string, string> = {
  interviews: "Kundeninterviews",
  willingness: "Zahlungsbereitschaft",
  pilot: "Pilot",
  built_unvalidated: "Feature ohne Validierung",
  nothing: "Noch nichts getestet",
};

const getValidationMeta = (percent: number) => {
  if (percent >= 80) return { label: "Hohe Validierung", Icon: ShieldCheck, colorClass: "text-primary", bgClass: "bg-primary/10 border-primary/20" };
  if (percent >= 50) return { label: "Mittlere Validierung", Icon: Shield, colorClass: "text-secondary", bgClass: "bg-secondary/10 border-secondary/20" };
  if (percent >= 20) return { label: "Niedrige Validierung", Icon: ShieldAlert, colorClass: "text-destructive/80", bgClass: "bg-destructive/5 border-destructive/15" };
  return { label: "Keine Validierung", Icon: ShieldAlert, colorClass: "text-destructive", bgClass: "bg-destructive/10 border-destructive/20" };
};

interface OnboardingEvidenceBlockProps {
  data: NonNullable<OnboardingContext["previousAttemptsData"]>;
  className?: string;
}

export const OnboardingEvidenceBlock = ({ data, className }: OnboardingEvidenceBlockProps) => {
  const { label, Icon, colorClass, bgClass } = getValidationMeta(data.validationPercent);
  const checksLabels = data.checks
    .filter(c => c !== "nothing")
    .map(c => TEST_LABELS[c] || c);
  const blockerLabel = data.blockerType
    ? `${data.blockerType === "intern" ? "Intern" : "Extern"}: ${data.blocker}`
    : data.blocker;

  return (
    <div className={cn("rounded-2xl border border-border/50 bg-card shadow-sm overflow-hidden", className)}>
      <div className="px-5 py-4 md:px-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
            Onboarding-Evidenz
          </p>
          <div className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold", bgClass)}>
            <Icon className={cn("w-3.5 h-3.5", colorClass)} />
            <span className={colorClass}>{data.validationPercent}% – {label}</span>
          </div>
        </div>

        {/* Checks */}
        {checksLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {checksLabels.map(c => (
              <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground border border-border/30">
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Signal */}
        {data.signal && data.signal.trim().length > 0 && (
          <div className="flex items-start gap-2.5">
            <Quote className="w-3.5 h-3.5 text-primary/60 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Stärkstes Marktsignal</p>
              <p className="text-sm text-foreground/85 leading-relaxed">„{data.signal}"</p>
            </div>
          </div>
        )}

        {/* Blocker */}
        {data.blocker && data.blocker.trim().length > 0 && (
          <div className="flex items-start gap-2.5">
            <Ban className="w-3.5 h-3.5 text-destructive/60 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Blocker</p>
              <p className="text-sm text-foreground/85 leading-relaxed">{blockerLabel}</p>
            </div>
          </div>
        )}

        {/* Low-evidence warning */}
        {data.validationPercent < 30 && (
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-destructive/5 border border-destructive/15">
            <AlertTriangle className="w-3.5 h-3.5 text-destructive flex-shrink-0" />
            <p className="text-xs font-medium text-destructive">
              Geringe Marktevidenz – erhöhte Annahme-Unsicherheit
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
